import { Fragment } from 'react';
import { observer } from 'mobx-react';
import cls from 'classnames';

import Icon from '@/common/components/Icon/Icon';
import { IconName } from '@/common/components/Icon/iconName';
import Checkbox from '@/common/components/inputs/Checkbox/Checkbox';
import Label, { SIZE_MEDIUM } from '@/common/components/Label/Label';
import { getAccessById, getAccessLabelAppearance, getAccessLabelIcon } from '@/security/constants/access';
import { getDataAccessById, getDataAccessIcon } from '@/security/constants/dataAccess';
import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { toProductDataAccess } from '@/security/helpers/toProductDataAccess';

import { useGlobalAccessModalStore } from '../../GlobalAccessModal/GlobalAccessModalStore';

import styles from './RecursiveUpdateSelect.module.css';

function RecursiveUpdateSelect() {
  const { accessValues, dataAccessValues, isUpdating, recursive, setRecursive, type } = useGlobalAccessModalStore();

  const accessValuesSet = new Set(accessValues);
  const dataAccessValuesSet = new Set(dataAccessValues);
  const isAccessSelected = accessValuesSet.size === 1;
  const isDataAccessSelected = dataAccessValuesSet.size === 1;
  const isRestricted = isAccessSelected && accessValues[0] === GlobalAccessLevel.Restricted;
  const isSelected = isAccessSelected && isDataAccessSelected;

  const accessLevel = accessValues[0];
  const recursiveDataAccessLevel = toProductDataAccess(dataAccessValues[0]);

  return (
    <div className={styles.recursive}>
      <Checkbox
        aria-label="Update access to existing solutions"
        checked={recursive}
        disabled={isUpdating || !isSelected}
        label={
          <div className={styles.recursiveLabel}>Update Access to Existing Solutions, Planned Diamonds and Scan</div>
        }
        onChange={() => setRecursive(!recursive)}
        type="checkbox"
      />

      <div className={cls(styles.description, { [styles.descriptionHidden]: !recursive || !isSelected })}>
        <div className={styles.recursiveDescriptionColumn}>
          <div className={styles.recursiveDescriptionLabel}>General</div>

          <div className={styles.recursiveDescriptionInput}>
            {isAccessSelected && (
              <Fragment>
                <Label
                  appearance={getAccessLabelAppearance(accessLevel)}
                  className={styles.recursiveDescriptionIcon}
                  size={SIZE_MEDIUM}
                  title={getAccessById(accessLevel, type)?.title}
                >
                  <Icon name={getAccessLabelIcon(accessLevel)} />
                </Label>

                <span aria-label="General access level">{getAccessById(accessLevel, type)?.title}</span>
              </Fragment>
            )}
          </div>
        </div>

        <div className={styles.recursiveDescriptionColumn}>
          <div className={styles.recursiveDescriptionLabel}>Cut Data</div>

          {isRestricted && (
            <div className={styles.recursiveRestricted}>All design data is available to company’ employees users</div>
          )}

          {!isRestricted && (
            <div className={styles.recursiveDescriptionInput}>
              {isDataAccessSelected && (
                <Fragment>
                  <Icon
                    className={styles.recursiveDescriptionSecurityIcon}
                    name={getDataAccessIcon(recursiveDataAccessLevel, 'product') as IconName}
                  />

                  <span aria-label="Cut data access level">{getDataAccessById(recursiveDataAccessLevel, 'product')?.title}</span>
                </Fragment>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default observer(RecursiveUpdateSelect);
